import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export enum AuthEventType {
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  LOGGED_IN = 'LOGGED_IN',
  LOGGED_OUT = 'LOGGED_OUT',
  USER_DATA_LOADED = 'USER_DATA_LOADED'
}

export interface AuthEvent {
  type: AuthEventType;
  payload?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthEventsService {
  private authEvents = new Subject<AuthEvent>();
  public authEvents$ = this.authEvents.asObservable();
  
  // Track user data loading state
  private userDataLoadedSubject = new BehaviorSubject<boolean>(false);
  public userDataLoaded$ = this.userDataLoadedSubject.asObservable();

  constructor() { }

  public emitEvent(event: AuthEvent): void {
    this.authEvents.next(event);
    
    // Update user data loaded state when appropriate events occur
    if (event.type === AuthEventType.USER_DATA_LOADED) {
      this.userDataLoadedSubject.next(true);
    } else if (event.type === AuthEventType.LOGGED_OUT || event.type === AuthEventType.SESSION_EXPIRED) {
      this.userDataLoadedSubject.next(false);
    }
  }
  
  /**
   * Check if user data is currently loaded
   * @returns Current state of user data loading
   */
  public isUserDataLoaded(): boolean {
    return this.userDataLoadedSubject.getValue();
  }
  
  /**
   * Wait for user data to be loaded
   * @returns Promise that resolves when user data is loaded
   */
  public waitForUserDataLoaded(): Promise<boolean> {
    // If data is already loaded, return immediately
    if (this.isUserDataLoaded()) {
      return Promise.resolve(true);
    }
    
    // Otherwise, wait for the data to be loaded
    return new Promise<boolean>((resolve) => {
      const subscription = this.userDataLoaded$.subscribe(isLoaded => {
        if (isLoaded) {
          subscription.unsubscribe();
          resolve(true);
        }
      });
    });
  }
}
